import { useQuery } from '@apollo/client';
import { useAuthentication } from '@context/businessContainer';
import {
  GET_CURRENT_PAYMENT_METHOD,
  GET_UPCOMING_INVOICE,
} from '@monofront/business/queries';
import {
  FirebasePaymentMethodsType,
  ICard,
  ISubscriptionPaymentMethodQueryResult,
  PaymentMethod,
} from '@monofront/types';
import { IGetUpcomingInvoiceQueryResult } from '@screens/ManageSubscription/type';
import { useEffect } from 'react';

interface IUserCardInformation {
  isCardInExpirationMonth: () => boolean;
  hasAtLeastOneMonthPastSinceExpirationDate: () => boolean;
  isDunning: boolean;
  refetchPaymentQuery: () => void;
  getPaymentQuery: ISubscriptionPaymentMethodQueryResult | undefined;
  currentPaymentMethodLoading: boolean;
}

export const useCardInformation = (): IUserCardInformation => {
  const { customer } = useAuthentication();
  const {
    data: getPaymentQuery,
    refetch: refetchPaymentQuery,
    loading: currentPaymentMethodLoading,
  } = useQuery<ISubscriptionPaymentMethodQueryResult>(
    GET_CURRENT_PAYMENT_METHOD,
    {
      fetchPolicy: 'cache-and-network',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    },
  );
  const { data: getUpcomingInvoice, loading: upcomingInvoiceLoading } =
    useQuery<IGetUpcomingInvoiceQueryResult>(GET_UPCOMING_INVOICE, {
      ssr: false,
      fetchPolicy: 'cache-and-network',
    });
  const invoiceData = getUpcomingInvoice?.getUpcomingInvoice;

  const isDunning =
    !upcomingInvoiceLoading && invoiceData?.status === 'past_due';

  const paymentMethod = getPaymentQuery?.getCurrentPaymentMethod;

  const isCard = (paymentMethod: PaymentMethod): paymentMethod is ICard => {
    return 'exp_year' in paymentMethod;
  };

  useEffect(() => {
    refetchPaymentQuery();
  }, [customer?.omsId]);

  const getPaymentMethodDetails = () => {
    if (
      paymentMethod &&
      paymentMethod.paymentType.toLocaleLowerCase() ===
        FirebasePaymentMethodsType.Card &&
      isCard(paymentMethod)
    ) {
      return {
        expYear: paymentMethod.exp_year,
        expMonth: paymentMethod.exp_month - 1,
      };
    }

    return null;
  };

  const getExpirationDate = () => {
    const paymentMethod = getPaymentMethodDetails();

    if (!paymentMethod) {
      return null;
    }

    return new Date(paymentMethod.expYear, paymentMethod.expMonth);
  };

  const isCardInExpirationMonth = () => {
    const expirationDate = getExpirationDate();

    if (!expirationDate) {
      return false;
    }

    const currentDate = new Date();

    return (
      expirationDate.getFullYear() === currentDate.getFullYear() &&
      expirationDate.getMonth() === currentDate.getMonth()
    );
  };

  const hasAtLeastOneMonthPastSinceExpirationDate = () => {
    const expirationDate = getExpirationDate();

    if (!expirationDate) {
      return false;
    }

    const currentDate = new Date();
    const yearDiff = currentDate.getFullYear() - expirationDate.getFullYear();
    const monthDiff = currentDate.getMonth() - expirationDate.getMonth();
    const totalMonthDiff = yearDiff * 12 + monthDiff;

    return totalMonthDiff >= 1;
  };

  return {
    isCardInExpirationMonth,
    hasAtLeastOneMonthPastSinceExpirationDate,
    isDunning,
    refetchPaymentQuery,
    getPaymentQuery,
    currentPaymentMethodLoading,
  };
};
